import React, { useEffect, createContext, useContext } from "react";
import { useDispatch } from "react-redux";
import { fetchPatient } from "../../store/slices/patientsSlice";
import Swal from "sweetalert2";

// Public storage base for previewing already-saved signature images
const STORAGE_URL = "https://trh-backend.clouxiplexi.com/storage/";
// Resolve a signature value (base64 data URI or stored relative path) to a src
export const resolveSignatureSrc = (v) => {
  if (!v) return null;
  return v.startsWith("data:") ? v : `${STORAGE_URL}${v}`;
};

// Context lets hoisted helper components read form state without being
// redefined on every render (which made inputs lose focus each keystroke).
const IcfFormCtx = createContext(null);

const Check = ({ name, label }) => {
  const { formData, setFormData, readOnly } = useContext(IcfFormCtx);
  return (
    <div className="col-md-4 col-sm-6">
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`chk_${name}`}
          checked={!!formData[name]}
          onChange={() => !readOnly && setFormData((prev) => ({ ...prev, [name]: !prev[name] }))}
          disabled={readOnly}
        />
        <label className="form-check-label" htmlFor={`chk_${name}`}>{label}</label>
      </div>
    </div>
  );
};

const SignRow = ({ role, sigField, dateField }) => {
  const { formData, setFormData, readOnly } = useContext(IcfFormCtx);
  const src = resolveSignatureSrc(formData[sigField]);

  const onUpload = (file) => {
    if (readOnly || !file) return;
    if (!file.type.startsWith("image/")) {
      Swal.fire({ title: "Invalid file", text: "Please upload an image file.", icon: "warning" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFormData((prev) => ({ ...prev, [sigField]: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="row g-3 align-items-center border-bottom py-3">
      <div className="col-md-3 fw-semibold">{role}</div>
      <div className="col-md-5">
        {src ? (
          <div className="d-flex align-items-center gap-2">
            <img src={src} alt={`${role} signature`} style={{ height: 60, maxWidth: 180, objectFit: "contain", border: "1px solid #dee2e6", borderRadius: 4, background: "#fff" }} />
            {!readOnly && (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setFormData((prev) => ({ ...prev, [sigField]: "" }))}>
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        ) : (
          <input type="file" accept="image/*" className="form-control form-control-sm" onChange={(e) => onUpload(e.target.files[0])} disabled={readOnly} />
        )}
      </div>
      <div className="col-md-4">
        <div className="form-floating form-floating-outline">
          <input type="date" className="form-control" value={formData[dateField] || ""} onChange={(e) => setFormData((prev) => ({ ...prev, [dateField]: e.target.value }))} disabled={readOnly} />
          <label>Date</label>
        </div>
      </div>
    </div>
  );
};

/**
 * Individual Client File Form
 * Comprehensive Psychiatric Rehabilitation Framework (CARF-Aligned)
 *
 * Layout mirrors IntegratedAssessmentForm.js (Bootstrap accordions + form-floating).
 * Parent scalar fields live directly on formData; the four repeaters
 * (goals, progress_notes, family_meetings, transition_activities) are arrays.
 */
const calculateAge = (dob) => {
  if (!dob) return "";
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const IndividualClientFileForm = ({ formData, setFormData, readOnly = false }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (formData.patient_id && !formData.client_name) {
      dispatch(fetchPatient(formData.patient_id))
        .unwrap()
        .then((patient) => {
          setFormData((prev) => ({
            ...prev,
            client_name: `${patient.first_name} ${patient.last_name}`,
            date_of_birth: patient.dob ? patient.dob.substring(0, 10) : "",
            age: patient.dob ? calculateAge(patient.dob) : "",
            gender: patient.gender ? patient.gender.toLowerCase() : "",
          }));
        })
        .catch((err) => console.error("Failed to prefill patient details in IndividualClientFileForm:", err));
    }
  }, [dispatch, formData.patient_id, formData.client_name, setFormData]);

  // ---------------- Scalar helpers ----------------
  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggle = (name) => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // ---------------- Repeater helpers ----------------
  const addRow = (key, blank) => {
    if (readOnly) return;
    setFormData((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), { id: Date.now() + Math.random(), ...blank }],
    }));
  };

  const removeRow = (key, id) => {
    if (readOnly) return;
    const list = formData[key] || [];
    if (list.length <= 1) {
      Swal.fire({ title: "Cannot Delete", text: "At least one entry must remain.", icon: "warning" });
      return;
    }
    Swal.fire({
      title: "Remove this entry?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    }).then((res) => {
      if (res.isConfirmed) {
        setFormData((prev) => ({ ...prev, [key]: prev[key].filter((r) => r.id !== id) }));
      }
    });
  };

  const changeRow = (key, id, field, value) => {
    if (readOnly) return;
    setFormData((prev) => ({
      ...prev,
      [key]: prev[key].map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    }));
  };

  const goals = formData.goals || [];
  const progressNotes = formData.progress_notes || [];
  const familyMeetings = formData.family_meetings || [];
  const transitionActivities = formData.transition_activities || [];

  return (
    <IcfFormCtx.Provider value={{ formData, setFormData, readOnly }}>
    <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto", scrollbarWidth: "thin" }}>
      <div className="accordion" id="icfAccordion">
        {/* ============================================================= */}
        {/* Accordion 1 — Client Identification & Intake */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#icfClientId">
              1. Client Identification &amp; Intake
            </button>
          </h2>
          <div id="icfClientId" className="accordion-collapse collapse show">
            <div className="accordion-body pt-3">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="client_name" value={formData.client_name || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Client Name</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="client_file_no" value={formData.client_file_no || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Client ID / File No</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="date_of_birth" value={formData.date_of_birth || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date of Birth</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="age" value={formData.age || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Age</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <select className="form-select" name="gender" value={formData.gender?.toLowerCase() || ""} onChange={handleChange} disabled={readOnly}>
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    <label>Gender</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="diagnosis" value={formData.diagnosis || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Diagnosis (if applicable)</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="date_of_enrollment" value={formData.date_of_enrollment || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date of Enrollment</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="consultant_psychiatrist" value={formData.consultant_psychiatrist || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Consultant Psychiatrist</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="consultant_designation" value={formData.consultant_designation || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Consultant Designation</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="case_manager_assigned" value={formData.case_manager_assigned || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Case Manager Assigned</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="prp_assigned" value={formData.prp_assigned || ""} onChange={handleChange} disabled={readOnly} />
                    <label>PRP Assigned</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="family_representative" value={formData.family_representative || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Family Representative</label>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="chk_consent_collaborative_care" checked={!!formData.consent_collaborative_care} onChange={() => toggle("consent_collaborative_care")} disabled={readOnly} />
                    <label className="form-check-label" htmlFor="chk_consent_collaborative_care">Consent for Collaborative Care Obtained</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 2 — Initial Multidimensional Assessment (Baseline) */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfAssessment">
              2. Initial Multidimensional Assessment (Baseline)
            </button>
          </h2>
          <div id="icfAssessment" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Clinical &amp; Functional</small>
              <div className="row g-2 mb-3">
                <Check name="assess_mental_health_status" label="Mental Health Status" />
                <Check name="assess_cognitive_functioning" label="Cognitive Functioning" />
                <Check name="assess_insight_motivation" label="Insight & Motivation Level" />
                <Check name="assess_risk_assessment" label="Risk Assessment" />
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Life Skills Domains</small>
              <div className="row g-2 mb-3">
                <Check name="assess_living_skills" label="Living Skills (ADLs, hygiene)" />
                <Check name="assess_learning_skills" label="Learning Skills" />
                <Check name="assess_working_skills" label="Working Skills" />
                <Check name="assess_social_skills" label="Social Skills" />
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Recovery Capital</small>
              <div className="row g-2 mb-3">
                <Check name="assess_family_support" label="Family Support" />
                <Check name="assess_financial_stability" label="Financial Stability" />
                <Check name="assess_housing" label="Housing" />
                <Check name="assess_community_integration" label="Community Integration" />
              </div>

              <div className="form-floating form-floating-outline">
                <textarea className="form-control" style={{ height: "120px" }} name="assessment_summary" value={formData.assessment_summary || ""} onChange={handleChange} disabled={readOnly}></textarea>
                <label>Assessment Summary / Clinician Notes</label>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 3 — Collaborative Goal Setting (repeater) */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfGoals">
              3. Collaborative Goal Setting
            </button>
          </h2>
          <div id="icfGoals" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-3">
                Each goal must begin with: <em>"I intend to…"</em> — written in the client's own words.
              </p>
              {goals.map((row, idx) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-light-subtle position-relative">
                  <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="mb-0 text-primary fw-bold">Goal {idx + 1}</h6>
                    <div>
                      {!readOnly && goals.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow("goals", row.id)}>
                          <i className="ri-delete-bin-line me-1"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "80px" }} value={row.intend_to || ""} onChange={(e) => changeRow("goals", row.id, "intend_to", e.target.value)} disabled={readOnly}></textarea>
                        <label>I intend to…</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.priority_rank || ""} onChange={(e) => changeRow("goals", row.id, "priority_rank", e.target.value)} disabled={readOnly} />
                        <label>Priority Rank</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="date" className="form-control" value={row.target_date || ""} onChange={(e) => changeRow("goals", row.id, "target_date", e.target.value)} disabled={readOnly} />
                        <label>Target Date</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.goal_achievement || ""} onChange={(e) => changeRow("goals", row.id, "goal_achievement", e.target.value)} disabled={readOnly} />
                        <label>Goal Achievement (%)</label>
                      </div>
                    </div>
                    <div className="col-md-3 d-flex align-items-center">
                      <div className="form-check mt-2">
                        <input className="form-check-input" type="checkbox" id={`smart_${row.id}`} checked={!!row.smart} onChange={(e) => changeRow("goals", row.id, "smart", e.target.checked)} disabled={readOnly} />
                        <label className="form-check-label" htmlFor={`smart_${row.id}`}>SMART Goal</label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button type="button" className="btn btn-outline-primary" onClick={() => addRow("goals", { intend_to: "", priority_rank: "", target_date: "", goal_achievement: "", smart: false })}>
                  <i className="ri-add-line me-1"></i> Add Goal
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 4 — Individualized Rehabilitation Plan (IRP) */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfIrp">
              4. Individualized Rehabilitation Plan (IRP)
            </button>
          </h2>
          <div id="icfIrp" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Program Selection</small>
              <div className="row g-2 mb-3">
                <Check name="irp_residential" label="Residential" />
                <Check name="irp_day_program" label="Day Program" />
                <Check name="irp_home_based" label="Home-Based / Outreach" />
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Program Customization</small>
              <div className="row g-2 mb-3">
                <Check name="irp_living_skills" label="Living Skills" />
                <Check name="irp_learning_skills" label="Learning Skills" />
                <Check name="irp_working_skills" label="Working Skills" />
                <Check name="irp_social_skills" label="Social Skills" />
                <Check name="irp_session_plan_codeveloped" label="Session Plan Co-developed" />
                <Check name="irp_frequency_duration_defined" label="Frequency & Duration Defined" />
              </div>

              <div className="row g-3">
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="session_frequency" value={formData.session_frequency || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Session Frequency</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="session_duration" value={formData.session_duration || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Session Duration</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="program_start_date" value={formData.program_start_date || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Program Start Date</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 5 — Evidence-Based Interventions */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfInterventions">
              5. Evidence-Based Interventions
            </button>
          </h2>
          <div id="icfInterventions" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Core Interventions</small>
              <div className="row g-2 mb-3">
                <Check name="int_psychoeducation" label="Psychoeducation" />
                <Check name="int_cbt" label="CBT-Based Interventions" />
                <Check name="int_social_skills_training" label="Social Skills Training" />
                <Check name="int_occupational_therapy" label="Occupational Therapy" />
                <Check name="int_behavioral_activation" label="Behavioral Activation" />
                <Check name="int_family_therapy" label="Family Therapy" />
                <Check name="int_vocational_training" label="Vocational Training" />
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Specialist Evidence-Based Programs</small>
              <div className="row g-2 mb-3">
                <Check name="int_imr" label="Illness Management & Recovery (IMR)" />
                <Check name="int_wrap" label="Wellness Recovery Action Planning (WRAP)" />
                <Check name="int_anti_stigma" label="Anti-Stigma Intervention" />
                <Check name="int_motivational_interviewing" label="Motivational Interviewing (MI)" />
              </div>

              <div className="form-floating form-floating-outline">
                <textarea className="form-control" style={{ height: "100px" }} name="intervention_notes" value={formData.intervention_notes || ""} onChange={handleChange} disabled={readOnly}></textarea>
                <label>Intervention Notes / Rationale</label>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 6 — Progress Monitoring (Twice Weekly Notes) — repeater */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfProgress">
              6. Progress Monitoring — Twice Weekly Notes
            </button>
          </h2>
          <div id="icfProgress" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              {progressNotes.map((row, idx) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-light-subtle position-relative">
                  <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="mb-0 text-primary fw-bold">Progress Note — Session {idx + 1}</h6>
                    <div>
                      {!readOnly && progressNotes.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow("progress_notes", row.id)}>
                          <i className="ri-delete-bin-line me-1"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="form-floating form-floating-outline">
                        <input type="date" className="form-control" value={row.note_date || ""} onChange={(e) => changeRow("progress_notes", row.id, "note_date", e.target.value)} disabled={readOnly} />
                        <label>Date</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.goal_achievement || ""} onChange={(e) => changeRow("progress_notes", row.id, "goal_achievement", e.target.value)} disabled={readOnly} />
                        <label>Goal Achievement (%)</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.participation || ""} onChange={(e) => changeRow("progress_notes", row.id, "participation", e.target.value)} disabled={readOnly} />
                        <label>Participation</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.symptoms || ""} onChange={(e) => changeRow("progress_notes", row.id, "symptoms", e.target.value)} disabled={readOnly}></textarea>
                        <label>Symptoms</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.functional_progress || ""} onChange={(e) => changeRow("progress_notes", row.id, "functional_progress", e.target.value)} disabled={readOnly}></textarea>
                        <label>Functional Progress</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.barriers || ""} onChange={(e) => changeRow("progress_notes", row.id, "barriers", e.target.value)} disabled={readOnly}></textarea>
                        <label>Barriers</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.adjustments_plan || ""} onChange={(e) => changeRow("progress_notes", row.id, "adjustments_plan", e.target.value)} disabled={readOnly}></textarea>
                        <label>Adjustments / Plan</label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button type="button" className="btn btn-outline-primary" onClick={() => addRow("progress_notes", { note_date: "", symptoms: "", functional_progress: "", goal_achievement: "", participation: "", barriers: "", adjustments_plan: "" })}>
                  <i className="ri-add-line me-1"></i> Add Progress Note
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 7 — Structured Goal Review Cycle + Family Meetings (repeater) */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfReview">
              7. Structured Goal Review Cycle
            </button>
          </h2>
          <div id="icfReview" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <div className="table-responsive mb-4">
                <table className="table table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "18%" }}>Timeline</th>
                      <th style={{ width: "22%" }}>Action</th>
                      <th>Outcome / Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Weekly</td>
                      <td>Progress Check</td>
                      <td><input type="text" className="form-control form-control-sm" name="review_weekly_outcome" value={formData.review_weekly_outcome || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>Monthly</td>
                      <td>Evaluation</td>
                      <td><input type="text" className="form-control form-control-sm" name="review_monthly_outcome" value={formData.review_monthly_outcome || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>Quarterly</td>
                      <td>Plan Revision</td>
                      <td><input type="text" className="form-control form-control-sm" name="review_quarterly_outcome" value={formData.review_quarterly_outcome || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>6-Monthly</td>
                      <td>Outcome Decision</td>
                      <td><input type="text" className="form-control form-control-sm" placeholder="Continue / Achieved / Modify / Drop" name="review_6monthly_outcome" value={formData.review_6monthly_outcome || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Monthly Family Meetings</small>
              <p className="text-muted small mb-3">
                Conducted monthly with client, family representative, PRP, and Case Manager to discuss progress, concerns, and rehabilitation planning.
              </p>
              {familyMeetings.map((row, idx) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-light-subtle position-relative">
                  <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="mb-0 text-primary fw-bold">Meeting {idx + 1}</h6>
                    <div>
                      {!readOnly && familyMeetings.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow("family_meetings", row.id)}>
                          <i className="ri-delete-bin-line me-1"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="form-floating form-floating-outline">
                        <input type="date" className="form-control" value={row.meeting_date || ""} onChange={(e) => changeRow("family_meetings", row.id, "meeting_date", e.target.value)} disabled={readOnly} />
                        <label>Meeting Date</label>
                      </div>
                    </div>
                    <div className="col-md-8">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.attendees || ""} onChange={(e) => changeRow("family_meetings", row.id, "attendees", e.target.value)} disabled={readOnly} />
                        <label>Attendees</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.key_concerns || ""} onChange={(e) => changeRow("family_meetings", row.id, "key_concerns", e.target.value)} disabled={readOnly}></textarea>
                        <label>Key Concerns / Updates</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.action_points || ""} onChange={(e) => changeRow("family_meetings", row.id, "action_points", e.target.value)} disabled={readOnly}></textarea>
                        <label>Action Points</label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button type="button" className="btn btn-outline-primary" onClick={() => addRow("family_meetings", { meeting_date: "", attendees: "", key_concerns: "", action_points: "" })}>
                  <i className="ri-add-line me-1"></i> Add Family Meeting
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 8 — Stage-Based Rehabilitation Model */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfStages">
              8. Stage-Based Rehabilitation Model
            </button>
          </h2>
          <div id="icfStages" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-3">Minimum 60% Goal Achievement required for stage advancement.</p>
              <div className="table-responsive mb-3">
                <table className="table table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Stage</th>
                      <th>Role</th>
                      <th>Activities</th>
                      <th style={{ width: "18%" }}>Date Achieved</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Stage 1</td>
                      <td>Client (Skill Building)</td>
                      <td>Structured program participation, core life skills</td>
                      <td><input type="date" className="form-control form-control-sm" name="stage1_date_achieved" value={formData.stage1_date_achieved || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>Stage 2</td>
                      <td>Observer</td>
                      <td>Observes sessions, builds insight, reduces stigma</td>
                      <td><input type="date" className="form-control form-control-sm" name="stage2_date_achieved" value={formData.stage2_date_achieved || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>Stage 3</td>
                      <td>Co-Facilitator</td>
                      <td>Assists sessions, applies skills</td>
                      <td><input type="date" className="form-control form-control-sm" name="stage3_date_achieved" value={formData.stage3_date_achieved || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>Stage 4</td>
                      <td>Facilitator</td>
                      <td>Leads sessions independently under PRP supervision</td>
                      <td><input type="date" className="form-control form-control-sm" name="stage4_date_achieved" value={formData.stage4_date_achieved || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="col-md-4">
                <div className="form-floating form-floating-outline">
                  <input type="text" className="form-control" name="duration_in_program" value={formData.duration_in_program || ""} onChange={handleChange} disabled={readOnly} />
                  <label>Duration in Program</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 9 — Transition & Reintegration Process */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfTransition">
              9. Transition &amp; Reintegration Process
            </button>
          </h2>
          <div id="icfTransition" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">9A. Graduated Transition Activities</small>
              {transitionActivities.map((row, idx) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-light-subtle position-relative">
                  <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="mb-0 text-primary fw-bold">Activity {idx + 1}</h6>
                    <div>
                      {!readOnly && transitionActivities.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow("transition_activities", row.id)}>
                          <i className="ri-delete-bin-line me-1"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.activity || ""} onChange={(e) => changeRow("transition_activities", row.id, "activity", e.target.value)} disabled={readOnly} />
                        <label>Activity</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="date" className="form-control" value={row.initiated_date || ""} onChange={(e) => changeRow("transition_activities", row.id, "initiated_date", e.target.value)} disabled={readOnly} />
                        <label>Initiated Date</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.frequency || ""} onChange={(e) => changeRow("transition_activities", row.id, "frequency", e.target.value)} disabled={readOnly} />
                        <label>Frequency</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.accompanied_by || ""} onChange={(e) => changeRow("transition_activities", row.id, "accompanied_by", e.target.value)} disabled={readOnly} />
                        <label>Accompanied By</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <select className="form-select" value={row.activity_status || ""} onChange={(e) => changeRow("transition_activities", row.id, "activity_status", e.target.value)} disabled={readOnly}>
                          <option value="">Select</option>
                          <option value="Pending">Pending</option>
                          <option value="Active">Active</option>
                          <option value="Complete">Complete</option>
                        </select>
                        <label>Status</label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button type="button" className="btn btn-outline-primary mb-4" onClick={() => addRow("transition_activities", { activity: "", initiated_date: "", frequency: "", accompanied_by: "", activity_status: "" })}>
                  <i className="ri-add-line me-1"></i> Add Transition Activity
                </button>
              )}

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">9B. Discharge Preparation</small>
              <div className="row g-2 mb-3">
                <Check name="disch_criteria_reviewed" label="Discharge criteria reviewed with client and family" />
                <Check name="disch_relapse_prevention" label="Relapse prevention plan completed (WRAP or equivalent)" />
                <Check name="disch_community_support" label="Community support services identified and linked" />
                <Check name="disch_medication_plan" label="Medication management plan confirmed" />
                <Check name="disch_crisis_contacts" label="Crisis contacts established" />
                <Check name="disch_followup_appointments" label="Outpatient / follow-up appointments scheduled" />
                <Check name="disch_housing_confirmed" label="Housing confirmed and safety-assessed" />
                <Check name="disch_family_orientation" label="Family orientation and briefing conducted" />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="planned_discharge_date" value={formData.planned_discharge_date || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Planned Discharge Date</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="reintegration_date" value={formData.reintegration_date || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Reintegration Date</label>
                  </div>
                </div>
              </div>

              <div className="form-floating form-floating-outline">
                <textarea className="form-control" style={{ height: "90px" }} name="reintegration_notes" value={formData.reintegration_notes || ""} onChange={handleChange} disabled={readOnly}></textarea>
                <label>Reintegration Notes</label>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 10 — Post-Reintegration Follow-Up */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfFollowup">
              10. Post-Reintegration Follow-Up
            </button>
          </h2>
          <div id="icfFollowup" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <div className="row g-2 mb-3">
                <Check name="follow_case_manager" label="Case Manager Assigned" />
                <Check name="follow_monitoring_plan" label="Monitoring Plan" />
                <Check name="follow_relapse_prevention" label="Relapse Prevention" />
                <Check name="follow_family_engagement" label="Family Engagement" />
                <Check name="follow_employment_tracking" label="Employment Tracking" />
              </div>
              <div className="form-floating form-floating-outline">
                <textarea className="form-control" style={{ height: "90px" }} name="followup_notes" value={formData.followup_notes || ""} onChange={handleChange} disabled={readOnly}></textarea>
                <label>Follow-Up Notes</label>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 11 — LEAP Domain */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfLeap">
              11. LEAP Domain (Lived Experience Advisory Panel)
            </button>
          </h2>
          <div id="icfLeap" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Eligibility</small>
              <div className="row g-2 mb-3">
                <Check name="leap_goal_achievement" label="≥ 60–80% Goal Achievement" />
                <Check name="leap_completed_facilitator" label="Completed Facilitator Stage" />
                <Check name="leap_demonstrates_insight" label="Demonstrates Insight, Stability, Stigma-Free Attitude, Emotional Regulation" />
                <Check name="leap_approved" label="Approved by PRP, Case Manager, and Family" />
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">LEAP Role Assigned</small>
              <div className="row g-2 mb-3">
                <Check name="leap_role_peer_support" label="Peer Support Facilitator" />
                <Check name="leap_role_facilitator" label="Facilitator / Co-Facilitator" />
                <Check name="leap_role_advisory" label="Advisory Panel Member" />
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Training Completed</small>
              <div className="row g-2 mb-3">
                <Check name="leap_train_facilitation" label="Facilitation Skills" />
                <Check name="leap_train_communication" label="Communication & Boundaries" />
                <Check name="leap_train_ethics" label="Ethics & Confidentiality" />
                <Check name="leap_train_mhfa" label="Mental Health First Aid" />
                <Check name="leap_train_selfcare" label="Self-Care & Relapse Prevention" />
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Participation Structure</small>
              <div className="row g-2 mb-3">
                <Check name="leap_supervisor_assigned" label="Supervisor Assigned" />
                <Check name="leap_role_defined" label="Role Defined" />
                <Check name="leap_engagement_plan" label="Engagement Plan" />
                <Check name="leap_supervision_scheduled" label="Supervision Sessions Scheduled" />
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Monitoring</small>
              <div className="table-responsive mb-3">
                <table className="table table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "20%" }}>Timeline</th>
                      <th style={{ width: "25%" }}>Focus</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Monthly</td>
                      <td>Role Performance</td>
                      <td><input type="text" className="form-control form-control-sm" name="leap_monitor_monthly" value={formData.leap_monitor_monthly || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>Quarterly</td>
                      <td>Leadership Growth</td>
                      <td><input type="text" className="form-control form-control-sm" name="leap_monitor_quarterly" value={formData.leap_monitor_quarterly || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>6-Monthly</td>
                      <td>Continuation Decision</td>
                      <td><input type="text" className="form-control form-control-sm" name="leap_monitor_6monthly" value={formData.leap_monitor_6monthly || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">LEAP Outcomes</small>
              <div className="row g-2 mb-3">
                <Check name="leap_out_continued_leadership" label="Continued Leadership" />
                <Check name="leap_out_paid_volunteer" label="Paid / Volunteer Roles" />
                <Check name="leap_out_workforce" label="Workforce Integration" />
                <Check name="leap_out_advocacy" label="Advocacy Roles" />
              </div>

              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Risk & Safeguards</small>
              <div className="row g-2">
                <Check name="leap_risk_burnout" label="Burnout Monitoring" />
                <Check name="leap_risk_emotional_support" label="Emotional Support" />
                <Check name="leap_risk_boundaries" label="Boundaries Enforcement" />
                <Check name="leap_risk_crisis_protocol" label="Crisis Protocol" />
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 12 — Documentation & Quality (CARF) */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfCarf">
              12. Documentation &amp; Quality (CARF Compliance)
            </button>
          </h2>
          <div id="icfCarf" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <div className="row g-2">
                <Check name="carf_person_centered" label="Person-Centered Documentation" />
                <Check name="carf_cultural_sensitivity" label="Cultural Sensitivity" />
                <Check name="carf_ethical_practice" label="Ethical Practice" />
                <Check name="carf_measurable_outcomes" label="Measurable Outcomes" />
                <Check name="carf_quality_improvement" label="Continuous Quality Improvement" />
                <Check name="carf_client_family_feedback" label="Client & Family Feedback" />
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 13 — Program Duration (Individualized) */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfDuration">
              13. Program Duration (Individualized)
            </button>
          </h2>
          <div id="icfDuration" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Duration informed by</small>
              <div className="row g-2 mb-3">
                <Check name="dur_strengths" label="Strengths" />
                <Check name="dur_motivation" label="Motivation" />
                <Check name="dur_social_support" label="Social Support" />
                <Check name="dur_financial_capacity" label="Financial Capacity" />
                <Check name="dur_occupational_readiness" label="Occupational Readiness" />
                <Check name="dur_recovery_capital" label="Recovery Capital" />
              </div>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="estimated_duration" value={formData.estimated_duration || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Estimated Duration</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="actual_end_date" value={formData.actual_end_date || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Actual End Date</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================= */}
        {/* Accordion 14 — Final Collaborative Sign-Off */}
        {/* ============================================================= */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#icfSignoff">
              Final Collaborative Sign-Off
            </button>
          </h2>
          <div id="icfSignoff" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-3">
                Upload a signature image for each signatory and record the date. Signatures are stored securely as image files.
              </p>
              <SignRow role="Client" sigField="client_signature" dateField="client_sign_date" />
              <SignRow role="Family Representative" sigField="family_rep_signature" dateField="family_rep_sign_date" />
              <SignRow role="Consultant Psychiatrist" sigField="psychiatrist_signature" dateField="psychiatrist_sign_date" />
              <SignRow role="PRP" sigField="prp_signature" dateField="prp_sign_date" />
              <SignRow role="Case Manager" sigField="case_manager_signature" dateField="case_manager_sign_date" />
              <SignRow role="LEAP Member (if applicable)" sigField="leap_member_signature" dateField="leap_member_sign_date" />
            </div>
          </div>
        </div>
      </div>
    </div>
    </IcfFormCtx.Provider>
  );
};

export default IndividualClientFileForm;
